import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Sidebar from "../components/Sidebar";
import "./MainLayout.css";

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

/**
 * The real app shell: header + a sidebar driven by GET /api/menu (see
 * MenuContext/Sidebar) instead of the Phase 3 placeholder. The sidebar
 * becomes an off-canvas drawer below the tablet breakpoint, toggled by the
 * hamburger button in the header (see MainLayout.css / Sidebar.css).
 */
export default function MainLayout({ children }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const displayName = user ? user.nickName || user.username : "";

  return (
    <div className="main-layout">
      <header className="main-header">
        <div className="main-header-left">
          <button
            type="button"
            className="sidebar-toggle"
            aria-label="Toggle menu"
            onClick={() => setSidebarOpen((prev) => !prev)}
          >
            <span />
            <span />
            <span />
          </button>
          <Link to="/home" className="brand">
            <img src="/images/digi-main.png" alt="Digicampus" className="brand-logo" />
          </Link>
          {user && user.instituteName && <span className="header-institute-name">{user.instituteName}</span>}
        </div>
        <div className="header-user" ref={menuRef}>
          <button
            type="button"
            className={`header-user-trigger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <span className="header-avatar">{initialsOf(displayName)}</span>
            <span className="header-user-text">
              <span className="header-user-name">{displayName}</span>
              {user && user.roleName && <span className="header-user-role">{user.roleName.replace(/^ROLE_/, "").replace(/_/g, " ")}</span>}
            </span>
            <svg className="header-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {menuOpen && (
            <div className="header-dropdown" role="menu">
              <div className="header-dropdown-info">
                <span className="header-dropdown-name">{displayName}</span>
                {user && user.username && <span className="header-dropdown-username">@{user.username}</span>}
              </div>
              <button type="button" className="header-dropdown-item" role="menuitem" onClick={logout}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>
      <div className="main-body">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
